import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    IconButton,
    Drawer,
    Tabs,
    Tab,
    Select,
    MenuItem,
    TextField,
    DialogContent,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Alert,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import GenericPage from '../../Commun/components/GenericPage';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import {
    Add,
    Save,
    Delete,
    ChevronLeft,
    ChevronRight,
    Settings,
    ViewQuilt,
    Description,
    Window as WindowIcon,
    DoorFront as DoorIcon,
    Architecture,
    Construction,
    DragHandle as DragHandleIcon
} from '@mui/icons-material';

import { projectsApi, Project } from '../services/projectsApi';
import { windowsApi, Window as WindowModel, WindowType, ShapeType, MaterialType } from '../services/windowsApi';
import { doorsApi, Door, DoorTypeEnum } from '../services/doorsApi';
import { profilesApi, Profile } from '../services/profilesApi';
import DesignCanvas, { Splitter, Sash } from '../components/DesignCanvas';
import Design3DViewer from '../components/Design3DViewer';
import TemplateLibrary, { Template } from '../components/TemplateLibrary';
import ComponentLibrary, { ComponentItem } from '../components/ComponentLibrary';
import DesignSheet from '../components/DesignSheet';
import DesignToolbox, { ToolType } from '../components/DesignToolbox';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import './Design.css'; // Keeping for some specific canvas styles if needed, but mostly overriding

const Design: React.FC = () => {
    const { t } = useLanguage();
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { id } = useParams<{ id: string }>();
    const location = useLocation() as { state: { projectId?: number } };
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [windows, setWindows] = useState<WindowModel[]>([]);
    const [doors, setDoors] = useState<Door[]>([]);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [selectedItem, setSelectedItem] = useState<WindowModel | Door | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
    const [designMode, setDesignMode] = useState<'window' | 'door'>('window');
    const [activeView, setActiveView] = useState<'design' | 'sheet'>('design');
    const [splitters, setSplitters] = useState<Splitter[]>([]);
    const [sashes, setSashes] = useState<Sash[]>([]);
    const [components, setComponents] = useState<ComponentItem[]>([]);
    const [quotation, setQuotation] = useState({
        unitPrice: 0,
        quantity: 1,
        discount: 0,
        notes: '',
    });
    const [hybridMaterials, setHybridMaterials] = useState<{ core: string; cladding: string; interior: string; hardware: string }>({
        core: 'PVC', cladding: 'Aluminum', interior: 'Wood', hardware: 'Steel'
    });
    const [surfaceMaterial, setSurfaceMaterial] = useState('ClearGlass');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
    const [formData, setFormData] = useState({
        windowType: WindowType.Casement,
        doorType: DoorTypeEnum.Single,
        shapeType: ShapeType.Rectangular,
        width: '1000',
        height: '1500',
        materialType: MaterialType.Aluminum,
        profileId: '',
    });
    const [activeTool, setActiveTool] = useState<ToolType>('select');
    const [leftPanelTab, setLeftPanelTab] = useState(0); // 0=List, 1=Tools
    const [splitRatio, setSplitRatio] = useState(0.5); // 50% for 2D, 50% for 3D
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadProjects();
        loadProfiles();
    }, []);

    useEffect(() => {
        if (isMobile) {
            setIsLeftPanelCollapsed(true);
            setIsRightPanelCollapsed(true);
        } else {
            setIsLeftPanelCollapsed(false);
            setIsRightPanelCollapsed(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (selectedProject) {
            loadWindows();
            loadDoors();
        }
    }, [selectedProject]);

    const loadProjects = async () => {
        try {
            const response = await projectsApi.getAll();
            setProjects(response.data || []);

            // Check URL param first, then location state
            let targetProjectId = id ? Number(id) : undefined;

            if (!targetProjectId && location.state?.projectId) {
                targetProjectId = Number(location.state.projectId);
            }

            if (targetProjectId) {
                const project = (response.data || []).find(p => p.id === targetProjectId);
                if (project) {
                    setSelectedProject(project);
                }
            }
        } catch (error) {
            console.error('Error loading projects:', error);
        }
    };

    const loadWindows = async () => {
        if (!selectedProject) return;
        try {
            const response = await windowsApi.getByProject(selectedProject.id);
            setWindows(response.data || []);
        } catch (error) {
            console.error('Error loading windows:', error);
        }
    };

    const loadDoors = async () => {
        if (!selectedProject) return;
        try {
            const response = await doorsApi.getByProject(selectedProject.id);
            setDoors(response.data || []);
        } catch (error) {
            console.error('Error loading doors:', error);
        }
    };

    const loadProfiles = async () => {
        try {
            const response = await profilesApi.getAll();
            setProfiles((response.data || []).filter((p: any) => p.isActive));
        } catch (error) {
            console.error('Error loading profiles:', error);
        }
    };

    const validateDesign = (): boolean => {
        const errors: string[] = [];

        if (!selectedProject) {
            errors.push(t('maker.design.validation.project'));
        }

        const width = parseFloat(formData.width);
        const height = parseFloat(formData.height);

        if (isNaN(width) || width <= 0) {
            errors.push(t('maker.design.validation.dimensions'));
        }

        if (isNaN(height) || height <= 0) {
            errors.push(t('maker.design.validation.dimensions'));
        }

        if (width > 5000 || height > 5000) {
            errors.push(t('maker.design.validation.dimensions'));
        }

        if (!formData.profileId && formData.materialType) {
            errors.push(t('maker.design.validation.profile'));
        }

        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleSaveDesign = async () => {
        if (!validateDesign()) {
            return;
        }

        if (!selectedProject) {
            blobticMessage.warning(t('common.messages.warning'), t('maker.design.validation.project'));
            return;
        }

        try {
            let finalSplitters = [...splitters];
            let finalSashes = [...sashes];
            const w = parseFloat(formData.width) || 1000;
            const h = parseFloat(formData.height) || 1500;

            if (finalSplitters.length === 0 && finalSashes.length === 0) {
                if (designMode === 'window') {
                    const type = formData.windowType;
                    if (type === WindowType.Sliding || type === WindowType.French || type === WindowType.ParallelSliding || type === WindowType.LiftSliding) {
                        finalSplitters = [{ id: `splitter-${Date.now()}`, type: 'vertical', position: 50 }];
                        finalSashes = [
                            { id: `sash-${Date.now()}-1`, x: 0, y: 0, width: w/2, height: h, type: 'opening' },
                            { id: `sash-${Date.now()}-2`, x: w/2, y: 0, width: w/2, height: h, type: 'opening' }
                        ];
                    } else if (type === WindowType.Accordion) {
                        finalSplitters = [
                            { id: `splitter-${Date.now()}-1`, type: 'vertical', position: 33.33 },
                            { id: `splitter-${Date.now()}-2`, type: 'vertical', position: 66.66 }
                        ];
                        finalSashes = [
                            { id: `sash-${Date.now()}-1`, x: 0, y: 0, width: w/3, height: h, type: 'opening' },
                            { id: `sash-${Date.now()}-2`, x: w/3, y: 0, width: w/3, height: h, type: 'opening' },
                            { id: `sash-${Date.now()}-3`, x: (w/3)*2, y: 0, width: w/3, height: h, type: 'opening' }
                        ];
                    } else {
                        finalSashes = [{ id: `sash-${Date.now()}`, x: 0, y: 0, width: w, height: h, type: 'opening' }];
                    }
                } else {
                    const type = formData.doorType;
                    if (type === DoorTypeEnum.Double || type === DoorTypeEnum.Sliding || type === DoorTypeEnum.Folding) {
                        finalSplitters = [{ id: `splitter-${Date.now()}`, type: 'vertical', position: 50 }];
                        finalSashes = [
                            { id: `sash-${Date.now()}-1`, x: 0, y: 0, width: w/2, height: h, type: 'opening' },
                            { id: `sash-${Date.now()}-2`, x: w/2, y: 0, width: w/2, height: h, type: 'opening' }
                        ];
                    } else {
                        finalSashes = [{ id: `sash-${Date.now()}`, x: 0, y: 0, width: w, height: h, type: 'opening' }];
                    }
                }
            }

            const customShapeData = JSON.stringify({
                splitters: finalSplitters,
                sashes: finalSashes,
                components: components.map(c => ({
                    id: c.id,
                    name: c.name,
                    category: c.category,
                    quantity: c.defaultQuantity || 1,
                    unitPrice: c.unitPrice,
                })),
                quotation,
                hybridMaterials,
                surfaceMaterial,
            });

            let createdItem: WindowModel | Door | null = null;

            if (designMode === 'window') {
                const result = await windowsApi.create({
                    projectId: selectedProject.id,
                    windowType: formData.windowType,
                    shapeType: formData.shapeType,
                    width: parseFloat(formData.width),
                    height: parseFloat(formData.height),
                    materialType: formData.materialType,
                    profileId: formData.profileId ? parseInt(formData.profileId) : undefined,
                    customShapeData,
                });

                if (result.message) {
                    blobticMessage.success(t('common.messages.success'), result.message);
                } else {
                    blobticMessage.success(t('common.messages.success'), t('maker.msg.success.create'));
                }

                await loadWindows();
                if (result.data) createdItem = result.data;
            } else {
                const result = await doorsApi.create({
                    projectId: selectedProject.id,
                    doorType: formData.doorType,
                    shapeType: formData.shapeType,
                    width: parseFloat(formData.width),
                    height: parseFloat(formData.height),
                    materialType: formData.materialType,
                    profileId: formData.profileId ? parseInt(formData.profileId) : undefined,
                    customShapeData,
                });

                if (result.message) {
                    blobticMessage.success(t('common.messages.success'), result.message);
                } else {
                    blobticMessage.success(t('common.messages.success'), t('maker.msg.success.create'));
                }

                await loadDoors();
                if (result.data) createdItem = result.data;
            }

            setIsModalOpen(false);
            setSplitters([]);
            setSashes([]);
            setComponents([]);
            setQuotation({ unitPrice: 0, quantity: 1, discount: 0, notes: '' });
            setHybridMaterials({ core: 'PVC', cladding: 'Aluminum', interior: 'Wood', hardware: 'Steel' });
            setSurfaceMaterial('ClearGlass');

            if (createdItem) {
                setTimeout(() => {
                    handleItemSelect(createdItem!);
                }, 0);
            }
        } catch (error: any) {
            console.error('Error saving design:', error);
            blobticMessage.error(t('common.messages.error'), t('maker.msg.failedSave', error.message || 'Unknown error'));
        }
    };

    const handleTemplateSelect = (template: Template) => {
        try {
            console.log("Template selected:", template);
            setValidationErrors([]);
            setFormData({
                ...formData,
                windowType: template.windowType || WindowType.Casement,
                doorType: template.doorType || DoorTypeEnum.Single,
                shapeType: template.shapeType ?? ShapeType.Rectangular,
                width: (template.defaultWidth || 1000).toString(),
                height: (template.defaultHeight || 2000).toString(),
            });
            
            setSplitters((template as any).splitters || []);
            setSashes((template as any).sashes || []);

            setIsTemplateModalOpen(false);
            // Small timeout to ensure smooth transition between dialogs
            setTimeout(() => {
                setIsModalOpen(true);
            }, 100);
        } catch (error) {
            console.error("Error selecting template:", error);
        }
    };

    const handleAddComponent = (component: ComponentItem) => {
        const existingIndex = components.findIndex(c => c.id === component.id);
        if (existingIndex >= 0) {
            setComponents(components.filter((_, i) => i !== existingIndex));
        } else {
            setComponents([...components, component]);
        }
    };

    const handleExportCNC = () => {
        if (!selectedItem) {
            blobticMessage.warning(t('common.messages.warning'), t('maker.design.no_items'));
            return;
        }
        const cncContent = generateCNCFile(selectedItem, splitters, sashes);
        const blob = new Blob([cncContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${designMode === 'window' ? 'window' : 'door'}_${selectedItem.id}_cutting.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        blobticMessage.success(t('common.messages.success'), t('maker.msg.success.generate'));
    };

    const generateCNCFile = (item: WindowModel | Door, splitters: Splitter[], sashes: Sash[]): string => {
        // ... same logic as before ...
        let content = `; CNC Cutting File - ${designMode === 'window' ? 'Window' : 'Door'} #${item.id}\n`;
        content += `; Generated: ${new Date().toISOString()}\n`;
        content += `; Dimensions: ${item.width}mm x ${item.height}mm\n\n`;

        content += `; Main Frame\n`;
        content += `G0 X0 Y0\n`;
        content += `G1 X${item.width} Y0 F1000\n`;
        content += `G1 X${item.width} Y${item.height} F1000\n`;
        content += `G1 X0 Y${item.height} F1000\n`;
        content += `G1 X0 Y0 F1000\n\n`;

        if (splitters.length > 0) {
            content += `; Splitters\n`;
            splitters.forEach((splitter) => {
                if (splitter.type === 'vertical') {
                    const x = (item.width * splitter.position) / 100;
                    content += `G0 X${x} Y0\n`;
                    content += `G1 X${x} Y${item.height} F1000\n`;
                } else {
                    const y = (item.height * splitter.position) / 100;
                    content += `G0 X0 Y${y}\n`;
                    content += `G1 X${item.width} Y${y} F1000\n`;
                }
            });
            content += `\n`;
        }

        if (sashes.length > 0) {
            content += `; Sashes\n`;
            sashes.forEach((sash) => {
                content += `G0 X${sash.x} Y${sash.y}\n`;
                content += `G1 X${sash.x + sash.width} Y${sash.y} F1000\n`;
                content += `G1 X${sash.x + sash.width} Y${sash.y + sash.height} F1000\n`;
                content += `G1 X${sash.x} Y${sash.y + sash.height} F1000\n`;
                content += `G1 X${sash.x} Y${sash.y} F1000\n`;
            });
        }

        content += `\nM30 ; End of program\n`;
        return content;
    };

    const handleGenerateReports = () => {
        if (!selectedProject) {
            blobticMessage.warning(t('common.messages.warning'), t('maker.design.validation.project'));
            return;
        }
        blobticMessage.success(t('common.messages.success'), t('maker.msg.success.generate'));
    };

    const handleItemSelect = (item: WindowModel | Door) => {
        setSelectedItem(item);
        setFormData({
            windowType: 'windowType' in item ? item.windowType : WindowType.Casement,
            doorType: 'doorType' in item ? item.doorType : DoorTypeEnum.Single,
            shapeType: item.shapeType,
            width: item.width.toString(),
            height: item.height.toString(),
            materialType: item.materialType,
            profileId: item.profileId?.toString() || '',
        });

        if (item.customShapeData) {
            try {
                const customData = JSON.parse(item.customShapeData);
                if (customData.splitters) setSplitters(customData.splitters);
                if (customData.sashes) setSashes(customData.sashes);
                if (customData.components) {
                    const mappedComponents = customData.components.map((c: any) => ({
                        id: c.id,
                        name: c.name,
                        category: c.category,
                        icon: '📦', // Placeholder
                        description: c.name,
                        unitPrice: c.unitPrice,
                        defaultQuantity: c.quantity,
                    }));
                    setComponents(mappedComponents);
                    setComponents(mappedComponents);
                }
                if (customData.quotation) {
                    setQuotation(customData.quotation);
                } else {
                    setQuotation({ unitPrice: 0, quantity: 1, discount: 0, notes: '' });
                }
                if (customData.hybridMaterials) {
                    setHybridMaterials(customData.hybridMaterials);
                } else {
                    setHybridMaterials({ core: 'PVC', cladding: 'Aluminum', interior: 'Wood', hardware: 'Steel' });
                }
                if (customData.surfaceMaterial) {
                    setSurfaceMaterial(customData.surfaceMaterial);
                } else {
                    setSurfaceMaterial('ClearGlass');
                }
            } catch (e) {
                console.error('Error parsing custom shape data:', e);
            }
        }
        // Switch to tools tab when item selected
        setLeftPanelTab(1);
        // Also reset tool
        setActiveTool('select');
        
        // Auto-close panel on mobile so user can see the canvas
        if (isMobile) {
            setIsLeftPanelCollapsed(true);
        }
    };

    const getWindowTypeName = (type: WindowType) => {
        const names: Record<WindowType, string> = {
            [WindowType.Casement]: t('maker.design.type.casement'),
            [WindowType.TiltTurn]: t('maker.design.type.tilt_turn'),
            [WindowType.Sliding]: t('maker.design.type.sliding'),
            [WindowType.French]: t('maker.design.type.french'),
            [WindowType.Italian]: t('maker.design.type.italian'),
            [WindowType.Awning]: t('maker.design.type.awning'),
            [WindowType.ParallelSliding]: t('maker.design.type.parallel_sliding'),
            [WindowType.Pivot]: t('maker.design.type.pivot'),
            [WindowType.Accordion]: t('maker.design.type.accordion'),
            [WindowType.LiftSliding]: t('maker.design.type.lift_sliding'),
        };
        return names[type] || t('maker.design.type.unknown');
    };

    const getDoorTypeName = (type: DoorTypeEnum) => {
        const names: Record<DoorTypeEnum, string> = {
            [DoorTypeEnum.Single]: t('maker.design.type.single'),
            [DoorTypeEnum.Double]: t('maker.design.type.double'),
            [DoorTypeEnum.Sliding]: t('maker.design.type.sliding'),
            [DoorTypeEnum.Folding]: t('maker.design.type.folding'),
            [DoorTypeEnum.Revolving]: t('maker.design.type.revolving'),
        };
        return names[type] || t('maker.design.type.unknown');
    };

    const headerActions = (
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
            {isMobile && (
                <IconButton onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)} color={!isLeftPanelCollapsed ? 'primary' : 'default'} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <ViewQuilt />
                </IconButton>
            )}
            <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 200 } }}>
                <InputLabel>{t('maker.design.select_project')}</InputLabel>
                <Select
                    value={selectedProject?.id || ''}
                    label={t('maker.design.select_project')}
                    onChange={(e) => {
                        const project = projects.find(p => p.id === Number(e.target.value));
                        setSelectedProject(project || null);
                        setSelectedItem(null);
                        setSplitters([]);
                        setSashes([]);
                        setComponents([]);
                        setHybridMaterials({ core: 'PVC', cladding: 'Aluminum', interior: 'Wood', hardware: 'Steel' });
                        setSurfaceMaterial('ClearGlass');
                        setLeftPanelTab(0);
                    }}
                >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Button variant="outlined" startIcon={<Architecture />} onClick={() => setIsTemplateModalOpen(true)}>
                {t('maker.design.templates')}
            </Button>
            <Button variant="outlined" startIcon={<Construction />} onClick={() => setIsComponentModalOpen(true)}>
                {t('maker.design.components')}
            </Button>
            {selectedItem && (
                <>
                    <Button variant="outlined" onClick={handleExportCNC} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>{t('maker.design.export_cnc')}</Button>
                    <Button variant="outlined" onClick={handleGenerateReports} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>{t('maker.design.reports')}</Button>
                </>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={() => {
                setValidationErrors([]);
                setSplitters([]);
                setSashes([]);
                setIsModalOpen(true);
            }} disabled={!selectedProject}>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{t('maker.design.new')}</Box>
            </Button>
            {isMobile && selectedItem && (
                <IconButton onClick={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)} color={!isRightPanelCollapsed ? 'primary' : 'default'} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Settings />
                </IconButton>
            )}
        </Box>
    );

    const mappedSashes = sashes.map(s => {
        if (s.type === 'fixed') return s;
        let newType = s.type;
        const currentWindowType = selectedItem && 'windowType' in selectedItem ? selectedItem.windowType : (designMode === 'window' ? formData.windowType : undefined);
        const currentDoorType = selectedItem && 'doorType' in selectedItem ? selectedItem.doorType : (designMode === 'door' ? formData.doorType : undefined);
        
        if (currentWindowType !== undefined) {
            if (currentWindowType === WindowType.Sliding || currentWindowType === WindowType.ParallelSliding) newType = 'sliding';
            else if (currentWindowType === WindowType.LiftSliding) newType = 'lift_sliding';
            else if (currentWindowType === WindowType.Pivot) newType = 'pivot';
            else if (currentWindowType === WindowType.Accordion) newType = 'accordion';
            else if (currentWindowType === WindowType.TiltTurn || currentWindowType === WindowType.Awning) newType = 'tilt';
            else newType = 'casement';
        } else if (currentDoorType !== undefined) {
            if (currentDoorType === DoorTypeEnum.Sliding) newType = 'sliding';
            else if (currentDoorType === DoorTypeEnum.Folding) newType = 'accordion';
            else if (currentDoorType === DoorTypeEnum.Revolving) newType = 'pivot';
            else newType = 'casement';
        }
        return { ...s, type: newType };
    });

    return (
        <GenericPage
            title={t('maker.design.title')}
            additionalActions={headerActions}
            footerEnabled={false}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
                {/* Main Content */}
                <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', gap: 2, height: '100%', position: 'relative' }}>

                    {/* Left Panel: Items List */}
                    {(() => {
                        const leftContent = (
                            <>
                                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: (isLeftPanelCollapsed && !isMobile) ? 'center' : 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                                    {!(isLeftPanelCollapsed && !isMobile) && <Typography variant="subtitle1" fontWeight="bold">{t('maker.design.items_list')}</Typography>}
                                    {!isMobile ? (
                                        <IconButton size="small" onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}>
                                            {isLeftPanelCollapsed ? <ChevronRight /> : <ChevronLeft />}
                                        </IconButton>
                                    ) : (
                                        <IconButton size="small" onClick={() => setIsLeftPanelCollapsed(true)}>
                                            <ChevronLeft />
                                        </IconButton>
                                    )}
                                </Box>

                                {!(isLeftPanelCollapsed && !isMobile) && (
                                    <>
                                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                            <Tabs value={leftPanelTab} onChange={(_, val) => setLeftPanelTab(val)}>
                                                <Tab label={t('maker.design.items_list')} />
                                                <Tab label="Tools" disabled={!selectedItem} />
                                            </Tabs>
                                        </Box>

                                        {leftPanelTab === 0 ? (
                                            <>
                                                <Tabs
                                                    value={designMode}
                                                    onChange={(_, val) => setDesignMode(val)}
                                                    variant="fullWidth"
                                                    sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
                                                >
                                                    <Tab icon={<WindowIcon />} label={t('maker.design.tab.windows', windows.length)} value="window" sx={{ minHeight: 40 }} />
                                                    <Tab icon={<DoorIcon />} label={t('maker.design.tab.doors', doors.length)} value="door" sx={{ minHeight: 40 }} />
                                                </Tabs>
                                                <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
                                                    {(designMode === 'window' ? windows : doors).map((item) => (
                                                        <ListItemButton
                                                            key={item.id}
                                                            selected={selectedItem?.id === item.id}
                                                            onClick={() => handleItemSelect(item)}
                                                        >
                                                            <ListItemText
                                                                primary={'windowType' in item ? getWindowTypeName(item.windowType) : getDoorTypeName(item.doorType)}
                                                                secondary={`${item.width} x ${item.height} mm`}
                                                            />
                                                        </ListItemButton>
                                                    ))}
                                                    {(designMode === 'window' ? windows : doors).length === 0 && (
                                                        <ListItem><ListItemText primary={t('maker.design.no_items')} /></ListItem>
                                                    )}
                                                </List>
                                            </>
                                        ) : (
                                            <DesignToolbox
                                                activeTool={activeTool}
                                                onToolChange={(tool) => {
                                                    if (tool.startsWith('shape-')) {
                                                        const newShape = tool === 'shape-rect' ? ShapeType.Rectangular
                                                            : tool === 'shape-round' ? ShapeType.Round
                                                                : ShapeType.Trapezoid;

                                                        setFormData({ ...formData, shapeType: newShape });
                                                        if (selectedItem) {
                                                            setSelectedItem({ ...selectedItem, shapeType: newShape });
                                                        }
                                                    } else {
                                                        setActiveTool(tool);
                                                    }
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        );

                        if (isMobile) {
                            return (
                                <Drawer
                                    anchor="left"
                                    open={!isLeftPanelCollapsed}
                                    onClose={() => setIsLeftPanelCollapsed(true)}
                                    PaperProps={{ sx: { width: '85%', maxWidth: 320, display: 'flex', flexDirection: 'column' } }}
                                >
                                    {leftContent}
                                </Drawer>
                            );
                        }

                        return (
                            <Paper elevation={2} sx={{
                                width: isLeftPanelCollapsed ? 50 : 280,
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'width 0.3s',
                                overflow: 'hidden'
                            }}>
                                {leftContent}
                            </Paper>
                        );
                    })()}

                    {/* Center: Canvas / 3D / Sheet */}
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 2 }}>
                        {!selectedProject ? (
                            <Paper sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h6" color="text.secondary">{t('maker.design.select_prompt')}</Typography>
                            </Paper>
                        ) : !selectedItem ? (
                            <Paper sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h6" color="text.secondary">{t('maker.design.select_item_prompt', 'Please select an item from the list or create a new one.')}</Typography>
                            </Paper>
                        ) : (
                            <>
                                <Box ref={containerRef} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
                                    <Paper sx={{ height: `${splitRatio * 100}%`, border: '1px solid', borderColor: 'divider', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {(() => {
                                            const currentProfileId = selectedItem ? selectedItem.profileId : (formData.profileId ? Number(formData.profileId) : undefined);
                                            const currentProfile = profiles.find(p => p.id === currentProfileId);
                                            const frameWidth = currentProfile ? currentProfile.width : 60;

                                            return (
                                                <DesignCanvas
                                                    width={selectedItem ? selectedItem.width : parseFloat(formData.width)}
                                                    height={selectedItem ? selectedItem.height : parseFloat(formData.height)}
                                                    shapeType={selectedItem ? selectedItem.shapeType : formData.shapeType}
                                                    frameThickness={frameWidth}
                                                    splitters={splitters}
                                                    sashes={mappedSashes}
                                                    onDimensionsChange={(w, h) => {
                                                        setFormData({ ...formData, width: w.toString(), height: h.toString() });
                                                        if (selectedItem) {
                                                            setSelectedItem({ ...selectedItem, width: w, height: h });
                                                        }
                                                    }}
                                                    onSplittersChange={setSplitters}
                                                    onSashesChange={setSashes}
                                                    themeMode={theme.palette.mode}
                                                    activeTool={activeTool}
                                                    components={components}
                                                    surfaceMaterial={surfaceMaterial}
                                                    componentLibraryOverlay={
                                                        <ComponentLibrary
                                                            onAddComponent={handleAddComponent}
                                                            selectedComponents={components}
                                                            hideAddButton={true}
                                                            hideActions={true}
                                                        />
                                                    }
                                                    toolboxOverlay={
                                                        <DesignToolbox
                                                            activeTool={activeTool}
                                                            onToolChange={(tool) => {
                                                                if (tool.startsWith('shape-')) {
                                                                    const newShape = tool === 'shape-rect' ? ShapeType.Rectangular
                                                                        : tool === 'shape-round' ? ShapeType.Round
                                                                            : ShapeType.Trapezoid;
                                                                    setFormData({ ...formData, shapeType: newShape });
                                                                    if (selectedItem) {
                                                                        setSelectedItem({ ...selectedItem, shapeType: newShape });
                                                                    }
                                                                } else {
                                                                    setActiveTool(tool);
                                                                }
                                                            }}
                                                        />
                                                    }
                                                />
                                            );
                                        })()}
                                    </Paper>

                                    {/* Draggable Splitter */}
                                    <Box
                                        sx={{
                                            height: 12,
                                            cursor: 'row-resize',
                                            bgcolor: 'background.default',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // Prevent text selection during drag
                                            const startY = e.clientY;
                                            const startRatio = splitRatio;
                                            const containerHeight = containerRef.current?.clientHeight || 800;

                                            const handleMouseMove = (moveEvent: MouseEvent) => {
                                                const deltaY = moveEvent.clientY - startY;
                                                const newRatio = startRatio + (deltaY / containerHeight);
                                                setSplitRatio(Math.max(0.1, Math.min(0.9, newRatio)));
                                            };
                                            const handleMouseUp = () => {
                                                document.removeEventListener('mousemove', handleMouseMove);
                                                document.removeEventListener('mouseup', handleMouseUp);
                                            };
                                            document.addEventListener('mousemove', handleMouseMove);
                                            document.addEventListener('mouseup', handleMouseUp);
                                        }}
                                    >
                                        <DragHandleIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </Box>

                                    <Paper sx={{ height: `calc(${(1 - splitRatio) * 100}% - 12px)`, border: '1px solid', borderColor: 'divider', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {(() => {
                                            const currentProfileId = selectedItem ? selectedItem.profileId : (formData.profileId ? Number(formData.profileId) : undefined);
                                            const currentProfile = profiles.find(p => p.id === currentProfileId);
                                            const frameWidth = currentProfile ? currentProfile.width : 60;
                                            const frameDepth = currentProfile ? currentProfile.height : 80;

                                            return (
                                                <Design3DViewer
                                                    width={selectedItem ? selectedItem.width : parseFloat(formData.width)}
                                                    height={selectedItem ? selectedItem.height : parseFloat(formData.height)}
                                                    depth={frameDepth}
                                                    frameWidth={frameWidth}
                                                    themeMode={theme.palette.mode}
                                                    splitters={splitters}
                                                    sashes={mappedSashes}
                                                    components={components}
                                                    surfaceMaterial={surfaceMaterial}
                                                    componentLibraryOverlay={
                                                        <ComponentLibrary
                                                            onAddComponent={handleAddComponent}
                                                            selectedComponents={components}
                                                            hideAddButton={true}
                                                            hideActions={true}
                                                        />
                                                    }
                                                    hybridMaterials={formData.materialType === MaterialType.Hybrid ? hybridMaterials : undefined}
                                                />
                                            );
                                        })()}
                                    </Paper>
                                </Box>
                            </>)}
                    </Box>

                    {/* Right Panel: Properties */}
                    {(() => {
                        const rightContent = (
                            <>
                                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: (isRightPanelCollapsed && !isMobile) ? 'center' : 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                                    {!isMobile ? (
                                        <IconButton size="small" onClick={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}>
                                            {isRightPanelCollapsed ? <Settings /> : <Settings />}
                                        </IconButton>
                                    ) : (
                                        <IconButton size="small" onClick={() => setIsRightPanelCollapsed(true)}>
                                            <ChevronRight />
                                        </IconButton>
                                    )}
                                    {!(isRightPanelCollapsed && !isMobile) && <Typography variant="subtitle1" fontWeight="bold">{t('maker.design.properties')}</Typography>}
                                </Box>

                                {!(isRightPanelCollapsed && !isMobile) && (
                                    <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {selectedItem ? (
                                    <>
                                        <DesignSheet
                                            item={selectedItem}
                                            components={components}
                                            quotation={quotation}
                                            hybridMaterials={hybridMaterials}
                                            surfaceMaterial={surfaceMaterial}
                                            onUpdateHybridMaterials={setHybridMaterials}
                                            onUpdateSurfaceMaterial={setSurfaceMaterial}
                                            onUpdateDimensions={(w, h) => {
                                                setFormData({ ...formData, width: w.toString(), height: h.toString() });
                                                setSelectedItem({ ...selectedItem, width: w, height: h });
                                            }}
                                            onUpdateQuotation={(field, value) => setQuotation({ ...quotation, [field]: value })}
                                            onUpdateItem={(updates: any) => {
                                                const updatedItem = { ...selectedItem, ...updates } as typeof selectedItem;
                                                setSelectedItem(updatedItem);
                                                if (updates.materialType) setFormData(prev => ({ ...prev, materialType: updates.materialType }));
                                                if (updates.profileId) setFormData(prev => ({ ...prev, profileId: updates.profileId.toString() }));
                                            }}
                                            profiles={profiles}
                                        />

                                        {validationErrors.length > 0 && (
                                            <Alert severity="error">
                                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                                    {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                                                </ul>
                                            </Alert>
                                        )}

                                        <Box sx={{ mt: 'auto', display: 'flex', gap: 1, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                            <Button variant="contained" color="primary" startIcon={<Save />} onClick={async () => {
                                                if (!validateDesign()) return;
                                                try {
                                                    const updateData = {
                                                        ...selectedItem,
                                                        width: parseFloat(formData.width),
                                                        height: parseFloat(formData.height),
                                                        materialType: formData.materialType,
                                                        profileId: formData.profileId ? parseInt(formData.profileId) : undefined,
                                                        customShapeData: JSON.stringify({ splitters, sashes, components, quotation, hybridMaterials, surfaceMaterial }),
                                                    };

                                                    if ('windowType' in selectedItem) {
                                                        const result = await windowsApi.update(selectedItem.id, updateData);
                                                        if (result.message) {
                                                            blobticMessage.success(t('common.messages.success'), result.message);
                                                        } else {
                                                            blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                                                        }
                                                        loadWindows();
                                                    } else {
                                                        const result = await doorsApi.update(selectedItem.id, updateData);
                                                        if (result.message) {
                                                            blobticMessage.success(t('common.messages.success'), result.message);
                                                        } else {
                                                            blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                                                        }
                                                        loadDoors();
                                                    }
                                                } catch (e: any) {
                                                    blobticMessage.error(t('common.messages.error'), t('common.messages.save_error') + ': ' + e.message);
                                                }
                                            }} fullWidth>
                                                {t('maker.design.action.save')}
                                            </Button>
                                            <Button variant="outlined" color="error" startIcon={<Delete />} onClick={async () => {
                                                if (!window.confirm(t('maker.design.delete.confirm'))) { return; }
                                                try {
                                                    if ('windowType' in selectedItem) {
                                                        const result = await windowsApi.delete(selectedItem.id);
                                                        if (result.message) {
                                                            blobticMessage.success(t('common.messages.success'), result.message);
                                                        } else {
                                                            blobticMessage.success(t('common.messages.success'), t('maker.msg.success.delete'));
                                                        }
                                                        loadWindows();
                                                    } else {
                                                        const result = await doorsApi.delete(selectedItem.id);
                                                        if (result.message) {
                                                            blobticMessage.success(t('common.messages.success'), result.message);
                                                        } else {
                                                            blobticMessage.success(t('common.messages.success'), t('maker.msg.success.delete'));
                                                        }
                                                        loadDoors();
                                                    }
                                                    setSelectedItem(null);
                                                } catch (e: any) {
                                                    blobticMessage.error(t('common.messages.error'), t('maker.msg.failedSave', e.message));
                                                }
                                            }}>
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                                        {t('maker.design.no_item_selected', 'No item selected')}
                                    </Typography>
                                )}
                                    </Box>
                                )}
                            </>
                        );

                        if (isMobile) {
                            return (
                                <Drawer
                                    anchor="right"
                                    open={!isRightPanelCollapsed}
                                    onClose={() => setIsRightPanelCollapsed(true)}
                                    PaperProps={{ sx: { width: '85%', maxWidth: 320, display: 'flex', flexDirection: 'column' } }}
                                >
                                    {rightContent}
                                </Drawer>
                            );
                        }

                        return (
                            <Paper elevation={2} sx={{
                                width: isRightPanelCollapsed ? 50 : 320,
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'width 0.3s',
                                overflow: 'hidden',
                                flexShrink: 0
                            }}>
                                {rightContent}
                            </Paper>
                        );
                    })()}

                </Box>
            </Box>

            {/* New Design Modal */}
            <RootDialogPage
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={t('maker.design.modal.title', designMode === 'window' ? t('maker.production.window_count', '').replace(/:\s*$/, '').trim() : t('maker.production.door_count', '').replace(/:\s*$/, '').trim())}
                maxWidth="sm"
                onAction={handleSaveDesign}
                actionLabel={t('maker.design.modal.create')}
            >
                <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>{t('maker.design.modal.type')}</InputLabel>
                        <Select
                            value={designMode === 'window' ? formData.windowType : formData.doorType}
                            label="Type"
                            onChange={(e) => designMode === 'window'
                                ? setFormData({ ...formData, windowType: Number(e.target.value) })
                                : setFormData({ ...formData, doorType: Number(e.target.value) })
                            }
                        >
                            {designMode === 'window' ?
                                Object.entries(WindowType).filter(([k]) => isNaN(Number(k))).map(([k, v]) => (
                                    <MenuItem key={v} value={v}>{getWindowTypeName(v as WindowType)}</MenuItem>
                                )) :
                                Object.entries(DoorTypeEnum).filter(([k]) => isNaN(Number(k))).map(([k, v]) => (
                                    <MenuItem key={v} value={v}>{getDoorTypeName(v as DoorTypeEnum)}</MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>{t('maker.design.modal.shape')}</InputLabel>
                        <Select
                            value={formData.shapeType}
                            label={t('maker.design.modal.shape')}
                            onChange={(e) => setFormData({ ...formData, shapeType: Number(e.target.value) })}
                        >
                            <MenuItem value={ShapeType.Rectangular}>{t('maker.shape.rectangular')}</MenuItem>
                            <MenuItem value={ShapeType.Round}>{t('maker.shape.round')}</MenuItem>
                            <MenuItem value={ShapeType.Trapezoid}>{t('maker.shape.trapezoid')}</MenuItem>
                            <MenuItem value={ShapeType.Custom}>{t('maker.shape.custom')}</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label={t('maker.design.field.width')} type="number" fullWidth value={formData.width} onChange={(e) => setFormData({ ...formData, width: e.target.value })} />
                        <TextField label={t('maker.design.field.height')} type="number" fullWidth value={formData.height} onChange={(e) => setFormData({ ...formData, height: e.target.value })} />
                    </Box>

                    <FormControl fullWidth>
                        <InputLabel>{t('maker.design.field.material')}</InputLabel>
                        <Select
                            value={formData.materialType}
                            label={t('maker.design.field.material')}
                            onChange={(e) => {
                                setFormData({ ...formData, materialType: Number(e.target.value), profileId: '' });
                            }}
                        >
                            <MenuItem value={MaterialType.Aluminum}>{t('maker.materials.type.aluminum')}</MenuItem>
                            <MenuItem value={MaterialType.PVC}>{t('maker.materials.type.pvc')}</MenuItem>
                            <MenuItem value={MaterialType.Wood}>{t('maker.materials.type.wood')}</MenuItem>
                            <MenuItem value={MaterialType.Steel}>{t('maker.materials.type.steel')}</MenuItem>
                            <MenuItem value={MaterialType.Hybrid}>{t('maker.materials.type.hybrid', 'Hybrid (Multi-layer)')}</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>{t('maker.design.field.profile')}</InputLabel>
                        <Select
                            value={formData.profileId}
                            label={t('maker.design.field.profile')}
                            onChange={(e) => setFormData({ ...formData, profileId: e.target.value as string })}
                        >
                            <MenuItem value=""><em>{t('common.actions.none', 'None')}</em></MenuItem>
                            {profiles.filter(p => p.materialType === formData.materialType).map(p => (
                                <MenuItem key={p.id} value={p.id.toString()}>{p.name} ({p.code})</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {validationErrors.length > 0 && (
                        <Alert severity="error">
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                            </ul>
                        </Alert>
                    )}
                </DialogContent>
            </RootDialogPage>

            {/* Template Library Modal */}
            <RootDialogPage
                open={isTemplateModalOpen}
                onClose={() => setIsTemplateModalOpen(false)}
                title={t('maker.design.templates')}
                fullWidth
                maxWidth="md"
            >
                <TemplateLibrary
                    onSelectTemplate={handleTemplateSelect}
                    designMode={designMode === 'window' ? 'window' : 'door'}
                    hideActions={true}
                />
            </RootDialogPage>

            {/* Component Library Modal */}
            <RootDialogPage
                open={isComponentModalOpen}
                onClose={() => setIsComponentModalOpen(false)}
                title={t('maker.design.components')}
                fullWidth
                maxWidth="md"
            >
                <ComponentLibrary
                    onAddComponent={handleAddComponent}
                    selectedComponents={components}
                    hideAddButton={true}
                    hideActions={true}
                />
            </RootDialogPage>

        </GenericPage>
    );
};

export default Design;
